import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { getExplorerGatewayApi } from '@squareup/dex-data-shared-explorer-gateway-api';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import {
  ApiExplorerContext,
  ApiExplorerContextProvider,
  ApiExplorerFieldDropdownOption,
  useApiExplorerAccessToken,
  useApiExplorerContext,
  useApiExplorerEndpoint,
  useApiExplorerEnvironment,
  useApiExplorerGraph,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import {
  collectAllPropertiesForEndpoint,
  convertPropertyArrayToNameSet,
} from '@squareup/dex-utils-oas';
import { getByJsonPointer } from '@squareup/dex-utils-shared-json';
import React, {
  FC,
  PropsWithChildren,
  useMemo,
  useEffect,
  useState,
} from 'react';

import { sourceIdDropdownOptions } from './helpers/dropdown-defaults';
import {
  EndpointLookupDetails,
  endpointLookup,
} from './helpers/field-value-lookup';

interface Props {}

const ApiExplorerFieldDropdownOptionsContainer: FC<
  PropsWithChildren<Props>
> = ({ children }) => {
  const contextValue = useApiExplorerContext();

  const accessToken = useApiExplorerAccessToken();
  const environment = useApiExplorerEnvironment();

  const endpoint = useApiExplorerEndpoint();
  const graph = useApiExplorerGraph();

  const [fieldDropdownOptionsMap, setFieldDropdownOptionsMap] = useState<
    Record<string, Array<ApiExplorerFieldDropdownOption>>
  >({});

  const [makeApiRequest] =
    getExplorerGatewayApi().useLazyApiExplorerRequestQuery();

  const { data: customAttributeSchema } =
    getDeveloperApi().useCustomAttributeSchemaQuery();

  useEffect(() => {
    if (!endpoint || !accessToken) {
      return;
    }

    const lookupResults: Record<
      string,
      Array<ApiExplorerFieldDropdownOption>
    > = {};

    async function makeRequest(
      endpointLookupDetails: EndpointLookupDetails,
      body?: Record<string, unknown>
    ) {
      try {
        const response = await makeApiRequest(
          {
            path: endpointLookupDetails.path,
            method: endpointLookupDetails.method,
            version: graph.squareVersion,
            accessToken,
            applicationType: environment,
            skipApiLogsTracking: true,
            body,
          },
          true
        );

        // Invalid status code
        if (!response?.data?.status || response.data.status >= 400) {
          return null;
        }

        const result = getByJsonPointer(
          response.data.body as Record<string, unknown>,
          endpointLookupDetails.resultJsonPointer
        );

        if (!result) {
          return null;
        }

        return result;
      } catch {
        return null;
      }
    }

    // Every time the user changes endpoints, we will look through all the fields, and prefill them as necessary
    const properties = convertPropertyArrayToNameSet(
      collectAllPropertiesForEndpoint(endpoint)
    );

    const requestsByEndpoint = new Map<
      EndpointLookupDetails,
      Promise<unknown | null>
    >();

    const requests: Array<Promise<unknown | null>> = [];

    // Simple scheduler to handle requests that depend on other requests.
    // This will fan out requests, and dedupe on using the `requestsByEndpoint` map.
    async function scheduleRequest(
      lookup: EndpointLookupDetails,
      lookupHash?: string
    ) {
      // Request is already queued. No need to go through dependency check
      const existingRequest = requestsByEndpoint.get(lookup);

      let body: Record<string, unknown> | undefined;
      if (lookup.dependsOn && lookup.dependsOn.length > 0 && !existingRequest) {
        const dependentRequests: Array<
          Promise<{ dependsOnEndpoint: EndpointLookupDetails; result: unknown }>
        > = [];
        for (const dependent of lookup.dependsOn) {
          // wait for the dependent lookup to finish
          const dependsOnLookup = requestsByEndpoint.get(dependent);
          const dependsOnRequest =
            dependsOnLookup || scheduleRequest(dependent);

          dependentRequests.push(
            dependsOnRequest.then((result) => ({
              dependsOnEndpoint: dependent,
              result,
            }))
          );
        }

        const result = await Promise.all(dependentRequests);

        if (result) {
          body = lookup.requestMapperFn
            ? lookup.requestMapperFn(result)
            : undefined;
        }
      }

      const fetchRequest: Promise<unknown | null> =
        existingRequest ?? makeRequest(lookup, body);

      requestsByEndpoint.set(lookup, fetchRequest);

      const result = await fetchRequest;
      if (result && lookupHash) {
        lookupResults[lookupHash] = lookup.mapperFn(result);
      }
      return result;
    }

    for (const propName of properties) {
      const lookup = endpointLookup[propName];
      const lookupHash = `${propName}-${accessToken}-${environment}`;

      if (!lookup || fieldDropdownOptionsMap[lookupHash]) {
        continue;
      }

      requests.push(scheduleRequest(lookup, lookupHash));
    }

    async function handleRequests() {
      const results = await Promise.allSettled(requests);

      if (results.some((result) => result.status === 'fulfilled')) {
        setFieldDropdownOptionsMap({
          ...fieldDropdownOptionsMap,
          ...lookupResults,
        });
      }
    }

    if (requests.length > 0) {
      handleRequests();
    }

    // We do not want to re-run this effect every time the makeApiRequest function or fieldDropdownOptionsMap changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, endpoint, environment, graph.squareVersion]);

  // Every time the user changes the endpoint, we want to prefill the input fields
  // that match our heuristic for the most common use cases.
  const combinedOptions = useMemo(() => {
    if (environment === ApplicationType.Sandbox) {
      return {
        ...sourceIdDropdownOptions,
        ...fieldDropdownOptionsMap,
      };
    } else {
      return fieldDropdownOptionsMap;
    }
  }, [fieldDropdownOptionsMap, environment]);

  const memoizedContext: ApiExplorerContext = useMemo(() => {
    return {
      ...contextValue,
      customAttributeSchema,
      apiExplorerFieldDropdownOptionsMap: combinedOptions,
    };
  }, [contextValue, customAttributeSchema, combinedOptions]);

  return (
    <ApiExplorerContextProvider value={memoizedContext}>
      {children}
    </ApiExplorerContextProvider>
  );
};

export { ApiExplorerFieldDropdownOptionsContainer };
